/**
 * Pit Link RBAC — 1:1 with the mobile app hierarchy.
 *
 *   WORKER          → own swing + own inspections + handovers + fault-finder + assets(read).
 *   SUPERVISOR      → all above + all-crew dashboard + PPFs + send-back + registration.
 *   SUPERINTENDENT  → all above + insights + owner-centre (except Mine Setups).
 *   OWNER / CONTROL → EVERYTHING (only role that can "Mine Setups": manage sites + site config).
 *
 * Every guard in the app should ask `can(user, "capability")` — never role
 * strings — so we can tweak the ladder in exactly one place.
 */

const OWNER_TIER = ["owner", "control", "admin"];
const SUPERINTENDENT_TIER = [...OWNER_TIER, "superintendent", "planner"];
const SUPERVISOR_TIER = [...SUPERINTENDENT_TIER, "supervisor"];

const CAPABILITIES = {
  // MINE SETUPS — Owner only per Josh's directive.
  manageSites: OWNER_TIER,
  siteConfig: OWNER_TIER,
  customModules: OWNER_TIER,
  // Fleet / people / templates — Superintendent+.
  manageAssets: SUPERINTENDENT_TIER,
  managePeople: SUPERINTENDENT_TIER,
  inspectionBuilder: SUPERINTENDENT_TIER,
  crews: SUPERINTENDENT_TIER,
  ownerCentre: SUPERINTENDENT_TIER, // Superintendent SEES the hub (Sites/SiteConfig cards hidden inside)
  allReports: SUPERINTENDENT_TIER,
  // Crew leadership — Supervisor+.
  crewDashboard: SUPERVISOR_TIER,
  sendBackInspection: SUPERVISOR_TIER,
  approveInspection: SUPERVISOR_TIER,
  registerUser: SUPERVISOR_TIER, // Supervisors can Add People per Josh
  addSwing: SUPERVISOR_TIER,      // Supervisors can Add Swings per Josh
  ppfsWrite: SUPERVISOR_TIER,
  handoversWrite: SUPERVISOR_TIER,
  // Custom Asset Types — Supervisor+ can add a new asset category
  // (e.g. "LV Trailer") to the site registry so it becomes available in
  // the Asset creation dropdown for everyone. Josh's Feb 23 directive.
  manageAssetTypes: SUPERVISOR_TIER,
  // Baseline — every authenticated user (WORKER included).
  swing: null,
  ownInspections: null,
  handovers: null,
  faultFinder: null,
  profile: null,
  assetsRead: null,
  insights: null, // Workers can view Insights per Josh
};

export function can(user, capability) {
  if (!user) return false;
  if (user.is_admin) return true; // hard override — internal admin
  const list = CAPABILITIES[capability];
  if (list === null) return true; // baseline for every logged-in user
  if (!list) return false; // unknown capability → deny
  const roleRaw = String(user.role || "").toLowerCase().trim();
  if (!roleRaw) return false;
  // Tighten role matching — split into tokens so a compound role like
  // "site_supervisor" resolves to ["site", "supervisor"] and matches
  // exactly against the tier list, but a decorated / masqueraded role
  // like "supervisor-viewer-shadow" only counts if one of its whole tokens
  // is a real tier member. This closes the previous substring-match
  // vulnerability (`role.includes("owner")` used to match "owner_bystander"
  // and similar). Josh's Feb 9 audit action item.
  const tokens = roleRaw.split(/[^a-z0-9]+/).filter(Boolean);
  if (tokens.length === 0) tokens.push(roleRaw);
  return list.some((r) => tokens.includes(r));
}

/**
 * Role banner label used in Profile / top-bar.
 */
export function tierOf(user) {
  if (!user) return "guest";
  if (can(user, "manageSites")) return "owner";
  if (can(user, "manageAssets")) return "superintendent";
  if (can(user, "crewDashboard")) return "supervisor";
  return "worker";
}
